import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface TourStep {
  title: string;
  description: string;
  target?: string;
}

const EMPLOYEE_STEPS: TourStep[] = [
  { title: 'Welcome to the ERP', description: 'This is your workspace for managing daily tasks, leave requests, attendance, and more.' },
  { title: 'My Workspace', description: 'Access your leave, attendance, expenses, timesheets, loans, and training from the sidebar under "My Workspace".' },
  { title: 'Approvals Inbox', description: 'If you have pending approvals, they appear in your Approvals Inbox in the Overview section.' },
  { title: 'Need Help?', description: 'Contact your manager or HR for assistance. The system supports both English and Arabic.' },
];

const MANAGER_STEPS: TourStep[] = [
  { title: 'Welcome, Manager', description: 'You have access to team management, approvals, and operational dashboards across your modules.' },
  { title: 'Approvals Inbox', description: 'Pending leave requests, expense claims, and purchase orders appear in your Approvals Inbox.' },
  { title: 'Team Views', description: 'On shared pages like Attendance and Leave, you see the Management View with team-level data and approval actions.' },
  { title: 'Dashboards', description: 'The Executive Dashboard gives you a cross-module overview of sales, production, delivery, and finance.' },
  { title: 'Navigation', description: 'Modules are organized by business process. Each section groups related pages for easy access.' },
];

const FINANCE_STEPS: TourStep[] = [
  { title: 'Welcome, Finance', description: 'Your finance workspace spans 7 sections: GL & Controls, AR, AP, Treasury, Compliance, Statements, and EPM.' },
  { title: 'Period Controls', description: 'Monthly close checklists, GL period locks, and fiscal cutoff dates are in Period & Fiscal Controls.' },
  { title: 'Approval Inbox', description: 'Voucher authorizations, expense claims, and PO approvals appear in your unified Approvals Inbox.' },
  { title: 'Reports', description: 'Financial statements, AR/AP aging, cash flow forecast, and variance dashboards are in the Financial Statements section.' },
];

const MANAGER_ROLES = [
  'super_admin', 'group_ceo', 'ceo_commercial', 'ceo_manufacturing', 'coo', 'chro', 'cfo', 'cbo',
  'hr_manager', 'finance_manager', 'sales_manager', 'procurement_manager', 'warehouse_manager',
  'production_manager', 'logistics_manager', 'quality_manager', 'engineering_manager',
  'project_manager', 'pmo_head', 'team_lead',
];
const FINANCE_ROLES = ['finance_manager', 'finance_ap', 'finance_ar', 'cfo'];

function getStepsForRole(role: string): TourStep[] {
  if (FINANCE_ROLES.includes(role)) return FINANCE_STEPS;
  if (MANAGER_ROLES.includes(role)) return MANAGER_STEPS;
  return EMPLOYEE_STEPS;
}

export default function OnboardingTour() {
  const { profile } = useAuth();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!profile) return;
    const key = `erp_tour_seen_${profile.id}`;
    try {
      if (!localStorage.getItem(key)) {
        setVisible(true);
      }
    } catch { /* ignore */ }
  }, [profile]);

  if (!visible || !profile) return null;

  const steps = getStepsForRole(profile.role);
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(`erp_tour_seen_${profile.id}`, 'true'); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-2 text-blue-400">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">Getting Started</span>
          </div>
          <button onClick={dismiss} className="text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{current.title}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{current.description}</p>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-800">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-blue-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {isLast ? (
              <button onClick={dismiss} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition-all">
                Get Started
              </button>
            ) : (
              <button onClick={() => setStep(s => s + 1)} className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition-all">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
